import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import DOMPurify from 'dompurify';
import type { BackendCalendarEvent } from '@classic/Components/EventsSlider/Schemas';
import styles from '@classic/Components/EventsSlider/Components/CalendarEvent.scss?inline';
import type {
    CalendarEventConfig,
    CalendarStyle,
    CalendarType,
    LayoutMode,
    EventDayShape,
} from '@classic/Components/EventsSlider/Types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

class CalendarEvent extends HTMLElement
{
    declare shadowRoot: ShadowRoot;
    private eventData: BackendCalendarEvent | null = null;
    private layoutMode: LayoutMode = 'inline';
    private eventDayShape: EventDayShape = 'rectangle';
    private customCSSVariables: Map<string, string> = new Map();
    private siteTimezone: string = 'UTC';
    private isMealEvent: boolean = false;

    // Define valid values as constants
    private static readonly VALID_LAYOUT_MODES: readonly LayoutMode[] = ['stacked', 'inline'];
    private static readonly VALID_EVENT_DAY_SHAPES: readonly EventDayShape[] = ['rectangle', 'circle'];
    private static readonly VALID_CALENDAR_STYLES: readonly CalendarStyle[] = ['grid'];
    private static readonly VALID_CALENDAR_TYPES: readonly CalendarType[] = ['meal', 'event'];

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.injectStyles();
    }

    private injectStyles(): void {
        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(styles);
        this.shadowRoot.adoptedStyleSheets = [styleSheet];
    }

    static get observedAttributes(): string[] {
        return [
            'date-shape',
            'layout-mode',
            'site-timezone',
        ];
    }

    connectedCallback(): void {
        // Only render if we have event data
        // This prevents errors when Slick carousel clones elements for infinite scrolling
        if (this.eventData) {
            this.render();
        }
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (oldValue === newValue) {
            return;
        }
        if (name === 'layout-mode') {
            this.setLayoutMode(newValue as LayoutMode);
        }
        if (name === 'date-shape') {
            this.setEventDayShape(newValue as EventDayShape);
        }
        if (name === 'site-timezone') {
            this.setSiteTimezone(newValue);
        }
    }

    public configure(config: CalendarEventConfig): void {
        // event data (required) — same validation as setEventData
        if (!config.eventData || !config.eventData.title || !config.eventData.start_date) {
            throw new Error('Invalid or incomplete event data');
        }
        this.eventData = config.eventData;
        if (config.siteTimezone) {
            this.siteTimezone = config.siteTimezone;
        }
        if (config.layoutMode && this.isValidLayoutMode(config.layoutMode)) {
            this.layoutMode = config.layoutMode;
        }
        if (config.eventDayShape && this.isValidEventDayShape(config.eventDayShape)) {
            this.eventDayShape = config.eventDayShape;
        }
        if (config.isMealEvent !== undefined) {
            this.isMealEvent = config.isMealEvent;
        }
        // Project parent context onto the host so the shadow stylesheet can use
        // :host([data-cal-style="grid"]) instead of :host-context(.calendar-events-style-grid),
        // which Safari does not support.
        if (config.calendarStyle && CalendarEvent.VALID_CALENDAR_STYLES.includes(config.calendarStyle)) {
            this.setAttribute('data-cal-style', config.calendarStyle);
        }
        if (config.calendarType && CalendarEvent.VALID_CALENDAR_TYPES.includes(config.calendarType)) {
            this.setAttribute('data-cal-type', config.calendarType);
        }
        if (config.cssVariables) {
            for (const [name, value] of Object.entries(config.cssVariables)) {
                this.customCSSVariables.set(name, value);
                this.style.setProperty(name, value);
            }
        }
        this.render();
    }

    public setEventData(event: BackendCalendarEvent, siteTimezone?: string): void {
        if (!event || typeof event !== 'object') {
            throw new Error('Invalid event data');
        }
        // Validate required fields
        if (!event.title || !event.start_date) {
            throw new Error('Missing required event fields');
        }
        this.eventData = event;
        if (siteTimezone) {
            this.siteTimezone = siteTimezone;
        }
        this.render();
    }

    public setSiteTimezone(tz: string): void {
        if (!tz || typeof tz !== 'string') {
            return;
        }
        this.siteTimezone = tz;
        this.render();
    }

    private isValidLayoutMode(value: string): value is LayoutMode {
        return CalendarEvent.VALID_LAYOUT_MODES.includes(value as LayoutMode);
    }

    private isValidEventDayShape(value: string): value is EventDayShape {
        return CalendarEvent.VALID_EVENT_DAY_SHAPES.includes(value as EventDayShape);
    }

    public setLayoutMode(mode: LayoutMode): void {
        if (!this.isValidLayoutMode(mode)) {
            return;
        }
        this.layoutMode = mode;
        this.render();
    }

    public setEventDayShape(shape: EventDayShape): void {
        if (!this.isValidEventDayShape(shape)) {
            return;
        }
        this.eventDayShape = shape;
        this.render();
    }

    public setMealCalendarMode(isMealEvent: boolean): void {
        this.isMealEvent = isMealEvent;
        this.render();
    }

    public setCSSVariable(name: string, value: string): void {
        this.customCSSVariables.set(name, value);
        this.style.setProperty(name, value);
    }

    public setCSSVariables(variables: Record<string, string>): void {
        Object.entries(variables).forEach(([name, value]) => {
            this.setCSSVariable(name, value);
        });
    }

    /**
     * Validates URLs including both absolute URLs (http/https) and internal paths.
     * Internal paths starting with /event are considered valid.
     * Uses the native URL API for robust absolute URL validation.
     */
    private isValidUrl(value: string): boolean {
        if (!value || typeof value !== 'string' || value.trim() === '') {
            return false;
        }

        const trimmedValue = value.trim();

        // Check for internal paths starting with /event (e.g., /event_view?id=123)
        if (/^\/event/.test(trimmedValue)) {
            return true;
        }

        // Use native URL API for absolute URL validation - more robust than regex
        // Only accept http and https protocols
        try {
            const url = new URL(trimmedValue);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            return false;
        }
    }

    private applyCSSVariables(): void {
        this.customCSSVariables.forEach((value, name) => {
            this.style.setProperty(name, value);
        });
    }

    private formatDate(
        dateString: string,
        siteTz: string = 'UTC',
        allDay: boolean = false,
    ): { weekday: string; month: string; day: string } {
        const date = !allDay ? dayjs(dateString).tz(siteTz) : dayjs(dateString);

        return {
            weekday: date.format('dddd'),
            month: date.format('MMM'),
            day: date.format('DD'),
        };
    }

    private formatTime(
        startDateTime: string | null,
        endDateTime: string | null,
        tzString: string = 'UTC',
        allDay: boolean = false,
    ): string {
        if (allDay) {
            return 'All Day';
        }

        if (!startDateTime && !endDateTime) {
            return '';
        }

        const dayObject = dayjs(startDateTime);
        const endDayObject = dayjs(endDateTime);

        if (startDateTime && endDateTime) {
            return `${this.formatTimeString(dayObject, tzString)} - ${this.formatTimeString(endDayObject, tzString)}`;
        }

        if (startDateTime) {
            return this.formatTimeString(dayObject, tzString);
        }

        return '';
    }

    private formatTimeString(dateTime: dayjs.Dayjs, tzString: string = 'UTC'): string {
        return dateTime.tz(tzString).format('h:mm A');
    };

    private render(): void {
        if (!this.eventData) {
            this.shadowRoot.innerHTML = 'No Event Data';
            return;
        }

        try {
            this.applyCSSVariables();

            const { weekday, month, day } = this.formatDate(
                this.eventData.start_datetime,
                this.siteTimezone,
                this.eventData.all_day,
            );

            this.shadowRoot.innerHTML = `
                <div class="calendar-event-container calendar-event-container__${this.layoutMode}">
                    <div class="event-date-container calendar-event-date__${this.eventDayShape}">
                        <div class="calendar-event-date__weekday">${weekday}</div>
                        <div class="calendar-event-date__month">${month}</div>
                        <div class="calendar-event-date__day">${day}</div>
                    </div>
                    <div class="event-details-container">
                        ${this.isMealEvent ? this.renderMealCalendarDetails() : this.renderEventDetails()}
                    </div>
                </div>
            `;
        } catch {
            this.shadowRoot.innerHTML = '<div class="error">Failed to load event</div>';
        }
    }

    private renderEventDetails(): string {
        const timeString = this.formatTime(
            this.eventData!.start_datetime,
            this.eventData!.end_datetime,
            this.siteTimezone,
            this.eventData!.all_day,
        );

        const titleHtml = this.eventData?.link && this.isValidUrl(this.eventData.link)
            ? `<a href="${this.eventData.link}" tabindex="-1">${DOMPurify.sanitize(this.eventData.title)}</a>`
            : DOMPurify.sanitize(this.eventData!.title);

        return `
            <div class="event-title">${titleHtml}</div>
            ${timeString ? `<div class="event-time">${timeString}</div>` : ''}
        `;
    }

    private renderMealCalendarDetails(): string {
        return `<div class="event-description">${DOMPurify.sanitize(this.eventData!.description)}</div>`;
    }
}

// Define the custom element
if (!customElements.get('calendar-event')) {
    customElements.define('calendar-event', CalendarEvent);
}

export default CalendarEvent;
